import { TimeZone } from '@app/shared/types/enums/time-zone';
import { IANA_TIME_ZONES } from '@app/shared/utils/time-utils';

export interface CivilYmd {
  year: number;
  month: number;
  day: number;
}

export interface WeekendPresence {
  saturday: boolean;
  sunday: boolean;
}

export interface WeekendFlagInference {
  includeSaturday: boolean | null;
  includeSunday: boolean | null;
  saturdayInRange: boolean;
  sundayInRange: boolean;
  coverageComplete: boolean;
}

export function civilYmdInTimeZone(instant: Date, timeZone: TimeZone): CivilYmd {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: IANA_TIME_ZONES[timeZone],
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(instant);

  return {
    year: Number(parts.find(part => part.type === 'year')?.value),
    month: Number(parts.find(part => part.type === 'month')?.value),
    day: Number(parts.find(part => part.type === 'day')?.value)
  };
}

export function weekdayInTimeZone(instant: Date, timeZone: TimeZone): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: IANA_TIME_ZONES[timeZone],
    weekday: 'long'
  }).format(instant);
}

export function pickerDateForCivilDay(ymd: CivilYmd): Date {
  return new Date(ymd.year, ymd.month - 1, ymd.day);
}

export function pickerDateForInstant(instant: Date, timeZone: TimeZone | undefined): Date {
  if (!timeZone) {
    return new Date(instant.getFullYear(), instant.getMonth(), instant.getDate());
  }

  return pickerDateForCivilDay(civilYmdInTimeZone(instant, timeZone));
}

export function weekendDaysInLocalPickerRange(from: Date, to: Date): WeekendPresence {
  const presence: WeekendPresence = { saturday: false, sunday: false };
  const cursor = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());

  while (cursor.getTime() <= end.getTime()) {
    const weekday = cursor.getDay();
    if (weekday === 6) {
      presence.saturday = true;
    }
    if (weekday === 0) {
      presence.sunday = true;
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  return presence;
}

export function weekendDaysInTimeZoneRange(from: Date, to: Date, timeZone: TimeZone): WeekendPresence {
  const presence: WeekendPresence = { saturday: false, sunday: false };
  for (const ymd of eachCivilYmdInclusive(civilYmdInTimeZone(from, timeZone), civilYmdInTimeZone(to, timeZone))) {
    const weekday = weekdayInTimeZone(zonedNoonUtc(ymd, timeZone), timeZone);
    if (weekday === 'Saturday') {
      presence.saturday = true;
    }
    if (weekday === 'Sunday') {
      presence.sunday = true;
    }
  }
  return presence;
}

export function pickerCoversRegistrationRange(
  pickerStart: Date,
  pickerEnd: Date,
  rangeFrom: Date,
  rangeTo: Date,
  timeZone: TimeZone
): boolean {
  const windowStart = new Date(
    pickerStart.getFullYear(),
    pickerStart.getMonth(),
    pickerStart.getDate(),
    0,
    0,
    0,
    0
  ).getTime();
  const windowEnd = new Date(
    pickerEnd.getFullYear(),
    pickerEnd.getMonth(),
    pickerEnd.getDate(),
    23,
    59,
    59,
    999
  ).getTime();

  for (const ymd of eachCivilYmdInclusive(
    civilYmdInTimeZone(rangeFrom, timeZone),
    civilYmdInTimeZone(rangeTo, timeZone)
  )) {
    const noon = zonedNoonUtc(ymd, timeZone).getTime();
    if (noon < windowStart || noon > windowEnd) {
      return false;
    }
  }

  return true;
}

export function inferWeekendFlags(input: {
  registrationId: string | undefined;
  timeZone: TimeZone | undefined;
  rangeFrom: Date;
  rangeTo: Date;
  pickerStart: Date | undefined;
  pickerEnd: Date | undefined;
  siblingInstants: Date[];
}): WeekendFlagInference {
  const trusted =
    !!input.registrationId &&
    !!input.timeZone &&
    !!input.pickerStart &&
    !!input.pickerEnd &&
    pickerCoversRegistrationRange(input.pickerStart, input.pickerEnd, input.rangeFrom, input.rangeTo, input.timeZone);

  const inRange = input.timeZone
    ? weekendDaysInTimeZoneRange(input.rangeFrom, input.rangeTo, input.timeZone)
    : weekendDaysInLocalPickerRange(input.rangeFrom, input.rangeTo);

  if (!trusted || !input.timeZone) {
    return {
      includeSaturday: inRange.saturday ? null : false,
      includeSunday: inRange.sunday ? null : false,
      saturdayInRange: inRange.saturday,
      sundayInRange: inRange.sunday,
      coverageComplete: false
    };
  }

  const siblingWeekdays = new Set(input.siblingInstants.map(instant => weekdayInTimeZone(instant, input.timeZone)));

  return {
    includeSaturday: inRange.saturday ? siblingWeekdays.has('Saturday') : false,
    includeSunday: inRange.sunday ? siblingWeekdays.has('Sunday') : false,
    saturdayInRange: inRange.saturday,
    sundayInRange: inRange.sunday,
    coverageComplete: true
  };
}

function eachCivilYmdInclusive(from: CivilYmd, to: CivilYmd): CivilYmd[] {
  const days: CivilYmd[] = [];
  const cursor = new Date(Date.UTC(from.year, from.month - 1, from.day));
  const end = new Date(Date.UTC(to.year, to.month - 1, to.day));

  while (cursor.getTime() <= end.getTime()) {
    days.push({
      year: cursor.getUTCFullYear(),
      month: cursor.getUTCMonth() + 1,
      day: cursor.getUTCDate()
    });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return days;
}

function zonedNoonUtc(ymd: CivilYmd, timeZone: TimeZone): Date {
  const iana = IANA_TIME_ZONES[timeZone];
  for (let hour = 0; hour < 36; hour += 1) {
    const instant = new Date(Date.UTC(ymd.year, ymd.month - 1, ymd.day, hour, 0, 0));
    const local = civilYmdInTimeZone(instant, timeZone);
    const localHour = Number(
      new Intl.DateTimeFormat('en-US', {
        timeZone: iana,
        hour: '2-digit',
        hourCycle: 'h23'
      }).format(instant)
    );
    if (local.year === ymd.year && local.month === ymd.month && local.day === ymd.day && localHour === 12) {
      return instant;
    }
  }

  return new Date(Date.UTC(ymd.year, ymd.month - 1, ymd.day, 10, 0, 0));
}
